import { useState } from 'react';
import { REGIONS, type Region } from '@photos/shared';
import { useUpdateProfile } from '../../api/hooks';
import { useSession } from '../../state/session';
import { useToast } from '../../state/toasts';
import { ChurchOptions, FellowshipSelect, ServicePicker, useProfileTags } from '../GroupPickers';
import { PhoneField, type PhoneValue } from '../PhoneField';
import { Modal } from '../Modal';

/** "My profile": edit your own region, church, groups (used for upload suggestions and admin overviews) and phone. */
export function ProfileDialog({ onClose }: { onClose(): void }) {
  const { user } = useSession();
  const { churches } = useProfileTags();
  const [region, setRegion] = useState<Region>(user.region ?? 'East');
  const [church, setChurch] = useState(user.church);
  const [fellowship, setFellowship] = useState(user.fellowship);
  const [services, setServices] = useState(user.services);
  const [phone, setPhone] = useState<PhoneValue>({ e164: user.phone ?? null, valid: true });
  const update = useUpdateProfile();
  const toast = useToast();

  const save = () =>
    update.mutate(
      // The phone only when it changed (null clears it).
      { region, church, fellowship, services, ...(phone.e164 !== (user.phone ?? null) ? { phone: phone.e164 } : {}) },
      {
        onSuccess: () => {
          onClose();
          toast({ icon: 'check', title: 'Profile saved' });
        },
        onError: (e) => toast({ icon: 'info', title: 'Couldn’t save your profile', sub: e.message }),
      },
    );

  return (
    <Modal onClose={onClose} labelledBy="profile-title">
      <h3 id="profile-title">My profile</h3>
      <p className="help">Your region, church, groups and phone. They help with tagging and don’t change what you can see.</p>
      <div className="field">
        <label htmlFor="pf-region">Region</label>
        <select id="pf-region" className="inp" value={region} onChange={(e) => setRegion(e.target.value as Region)}>
          {REGIONS.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <label htmlFor="pf-church">Church</label>
        <select id="pf-church" className="inp" value={church} onChange={(e) => setChurch(e.target.value)}>
          <option value="">Not set</option>
          <ChurchOptions churches={churches} />
        </select>
      </div>
      <div className="field">
        <label htmlFor="pf-fellowship">Fellowship group</label>
        <FellowshipSelect id="pf-fellowship" className="inp" value={fellowship} onChange={setFellowship} label="Fellowship group" />
      </div>
      <div className="field">
        <label>
          Service groups <small style={{ color: 'var(--muted)', fontWeight: 400 }}>(any number)</small>
        </label>
        <ServicePicker value={services} onChange={setServices} label="Your service groups" />
      </div>
      <div className="field">
        <label htmlFor="pf-phone">
          Phone <small style={{ color: 'var(--muted)', fontWeight: 400 }}>(optional)</small>
        </label>
        <PhoneField id="pf-phone" initial={user.phone} onChange={setPhone} />
      </div>
      <div className="foot">
        <button type="button" className="btn" data-close onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="btn primary" disabled={update.isPending || !phone.valid} onClick={save}>
          Save
        </button>
      </div>
    </Modal>
  );
}
